import { FC, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import Select, { GroupBase, MultiValue, OptionsOrGroups } from "react-select";

import HubInputField from "../inputType";
import AfrezzaPrescription from "../afrezzaPrescription";
import ProvidePatientTreatment from "../addCase/providePatientTreatment";
import CurrentAndPriorTreatment from "../addCase/currentAndPriorTreatment";

import useFetch from "src/hooks/useFetch";
import { NYHA_CLASSES, PACK_OPTIONS } from "src/constants/cases";
import { APIEndpoints } from "src/types/apiTypes";
import { CaseStepTwoProps, ProviderDropdownResponse } from "./types";
import { DiagnosisResponseProps } from "../caseStepOne/types";
import { Link } from "react-router-dom";
import { format } from "date-fns";




const CaseStepTwo: FC<CaseStepTwoProps> = ({ control, isVisible, getValues, watch, setValue, trigger, resetField, setFocus, setSelectedDoctorObj, isCloneCase }): JSX.Element => {
  const practiceId = getValues("doctor_attributes.practice_id");
  const doctorId = getValues("doctor_attributes.global_doctor_id");

  const { data: rfcListing, mutate: getDiagnosis } =
    useFetch<DiagnosisResponseProps>(APIEndpoints.GET_DIAGNOSIS_DROPDOWN);

  const [selectedDiagnosisCodes, setSelectedDiagnosisCodes] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    if (isVisible) {
      setFocus && setFocus("prescription_attributes.condition");
    }
  }, [isVisible, setFocus]);

  const updateSelectedDiagnosisCodes = (vals: { value: string; label: string }[]) => {
    setSelectedDiagnosisCodes(vals);
  };

  useEffect(() => {
    getDiagnosis(null, null, {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const { data: globalDoctorsApiResponse, mutate: getGlobalDoctorsListing } =
    useFetch<ProviderDropdownResponse>(APIEndpoints.PROVIDER_DROPDOWN);

  useEffect(() => {
    if (practiceId) {
      getGlobalDoctorsListing({}, {}, { practice_id: practiceId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId]);

  useEffect(() => {
    if (doctorId) {
      const doctorObj = globalDoctorsApiResponse?.data.find((value) => value.id === doctorId);
      if (doctorObj) {
        setValue("doctor_fullname", doctorObj.fullname);
        setValue("doctor_firstlastname", doctorObj.first_last_name);
        setSelectedDoctorObj(doctorObj);
      }
    } else {
      setSelectedDoctorObj(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorId, globalDoctorsApiResponse]);





  return (
    <div className={`${!isVisible ? 'd-none' : ''}`} style={{ minHeight: "100vh" }}>
      <div className="card mb-3">
        <div className="card-body">
          <p>
            In order to process your request for benefit investigation, the following information must be completed. All information is
            kept confidential according to HIPAA guidelines. Please fill out the fields below as completely as possible. Missing fields
            may result in a delay to processing patient benefits in a timely manner.
          </p>

          <div className="row">
            <div className="col-lg-6">
              <Controller
                name="prescription_attributes.condition"
                control={control}
                defaultValue=""
                render={( { field, fieldState: { error } } ) => (
                  <HubInputField
                    inputId="prescription_attributes.condition"
                    inputLabel="Patient Diagnosis"
                    isRequired={true}
                    isRowBased={true}
                    isRowInsideAnother={true}
                    field={field}
                    error={error}
                    isDropdown={true}
                    isSearchable={false}
                    inputPlaceholder="Select Patient Diagnosis"
                    isTypeAhead
                    dropdownData={
                      NYHA_CLASSES.map( ( item ) => ( { id: item.id, name: item.name } ) ).map(
                        ( procedure ) => ( { value: procedure.id, label: procedure.name } )
                      ) as unknown as OptionsOrGroups<string, GroupBase<string>>
                    }
                  />
                )}
              />
              <div className="row mb-2">
                <label htmlFor="prescription_attributes.diagnosis_code" className="form-label col-lg-4 col-md-4 col-12">
                  Diagnosis Code *
                </label>
                <div className="col-lg-8 col-md-8 col-12">
                  <Controller
                    name="prescription_attributes.diagnosis_code"
                    control={control}
                    defaultValue={[]}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <Select
                          options={
                            (rfcListing && rfcListing.data && rfcListing.data.length > 0
                              ? rfcListing.data.map((option) => ({ value: option.code, label: option.fullname }))
                              : []) as unknown as OptionsOrGroups<string, GroupBase<string>>
                          }
                          components={{
                            MultiValueLabel: (props) => {
                              const opt = props.data as { value: string; label: string };
                              const found = selectedDiagnosisCodes.find((item) => item.value === opt.value);
                              return (
                                <div
                                  {...props.innerProps}
                                  className="p-1"
                                  style={{ fontSize: 12 }}
                                  title={found?.label ?? opt.label}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="bottom"
                                >
                                  {found?.value ?? opt.value}
                                </div>
                              );
                            }
                          }}
                          id="prescription_attributes.diagnosis_code"
                          isSearchable={false}
                          isMulti={true}
                          placeholder="Select diagnosis code"
                          classNames={{ control: () => `input-group ${error?.message ? "border-danger" : ""}` }}
                          {...field}
                          noOptionsMessage={() => "Select Option..."}
                          value={
                            field &&
                            field.value.map((item: string) => ({
                              value: item,
                              label: item,
                            })) as unknown as MultiValue<unknown>
                          }
                          onChange={(val) => {
                            updateSelectedDiagnosisCodes(
                              (val as MultiValue<{ value: string; label: string }>).map(
                                (item) => ({ value: item.value, label: item.label })
                              )
                            );
                            field.onChange(
                              (val as MultiValue<{ value: string; label: string }>).map((item) => item.value)
                            );
                          }}
                        />
                        {error && error.message ? (
                          <div className="text-danger text-small mt-2">{error.message}</div>
                        ) : null}
                      </>
                    )}
                  />
                </div>
              </div>
              <Controller
                name="prescription_attributes.packaging_size"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <HubInputField
                    inputId="prescription_attributes.packaging_size"
                    inputLabel="Package Size"
                    isRequired={true}
                    isRowBased={true}
                    isRowInsideAnother={true}
                    field={field}
                    error={error}
                    isDropdown={true}
                    isSearchable={false}
                    inputPlaceholder="Package Size"
                    dropdownData={
                      PACK_OPTIONS as unknown as OptionsOrGroups<string, GroupBase<string>>
                    }
                  />
                )}
              />
            </div>
            <div className="col-lg-6">
              <Controller
                name="prescription_attributes.note"
                control={control}
                defaultValue=""
                render={( { field, fieldState: { error } } ) => (
                  <div className="row mb-2">
                    <label htmlFor="prescription_attributes.note" className="form-label col-lg-4 col-md-4 col-12">Instructions for Patient</label>
                    <div className="col-lg-8 col-md-8 col-12">
                      <textarea
                        id="prescription_attributes.note"
                        className={`form-control ${error ? "border-danger" : ""}`}
                        style={{ resize: "none" }} rows={5}
                        {...field}
                        placeholder="Instructions Shared With Patient"
                      />
                    </div>
                    </div>
                )}
              />
            </div>
          </div>

          <AfrezzaPrescription control={control} watch={watch} setValue={setValue} getValues={getValues} resetField={resetField} isCloneCase={isCloneCase}/>

          <Controller
            name="prescription_attributes.written_date"
            control={control}
            defaultValue={format(new Date(), 'MM/dd/yyyy')}
            render={({ field: { value } }) => (
              <div className="row form-check d-flex">
                <label className="form-label col-12 col-md-4 col-lg-2"></label>
                <div className="col-12 col-md-8 col-lg-10">
                  <input className="form-check-input" type="checkbox" id="flexCheckIndeterminateDisabled" disabled checked/>
                  <label className="form-check-label" htmlFor="flexCheckIndeterminateDisabled">
                    Dispense as Written (written date will be {value})
                  </label>
                </div>
              </div>
            )}
          />

          <Controller
            name="prescription_attributes.drug_name"
            control={control}
            render={() => <></>}
          />
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-header">
          <h1 className="m-0">Provide Patient Training</h1>
        </div>
        <div className="card-body">
          <ProvidePatientTreatment control={control} watch={watch} setValue={setValue} getValues={getValues} resetField={resetField} />
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-header">
          <h1 className="m-0">Current and Prior Treatment</h1>
        </div>
        <div className="card-body">
          <CurrentAndPriorTreatment control={control} watch={watch} setValue={setValue} getValues={getValues} resetField={resetField} />
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-header">
          <h1 className="m-0">Prescriber Information</h1>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-9 col-lg-10">
              <Controller
                name="doctor_attributes.global_doctor_id"
                control={control}
                defaultValue={0}
                render={({ field, fieldState: { error } }) => (
                  <div className={"row"}>
                    <label className={"form-label col-lg-2 col-md-4 col-12 p-lg-0"}>Prescriber *</label>
                    <div className={"col-lg-10 col-md-8 col-12"}>
                      <HubInputField
                        inputId="doctor_attributes.global_doctor_id"
                        inputPlaceholder="Select a Prescriber..."
                        isRequired={true}
                        isRowBased={true}
                        field={field}
                        error={error}
                        isDropdown={true}
                        isSearchable={false}
                        isTypeAhead
                        dropdownData={
                          (globalDoctorsApiResponse && globalDoctorsApiResponse.data && globalDoctorsApiResponse.data.length > 0 ?
                            globalDoctorsApiResponse.data.map((provider) => ({ value: provider.id, label: provider.fullname }))
                            : []
                          ) as unknown as OptionsOrGroups<string, GroupBase<string>>
                        }
                      />
                    </div>
                  </div>
                )}
              />
            </div>
            <div className="col-md-3 col-lg-2">
              <Link to="/doctors/new" className="btn btn-primary">
                Add Prescriber
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseStepTwo;
